import { Loader2 } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

interface ProgressBannerProps {
  loaded: number;
  /** Upper bound (best known). When null, the bar is shown as indeterminate. */
  total?: number | null;
  label?: string;
  /** ISO day currently being fetched by the paginator. */
  currentDate?: string | null;
}

export function ProgressBanner({
  loaded,
  total,
  label = "Loading runs",
  currentDate,
}: ProgressBannerProps) {
  const hasTotal = typeof total === "number" && total > 0;
  // Clamp at 99% while the query is still in flight — the banner unmounts on success.
  const percent = hasTotal ? Math.min(99, Math.floor((loaded / (total as number)) * 100)) : null;

  return (
    <Alert>
      <Loader2 className="size-4 animate-spin" />
      <AlertTitle>{label}</AlertTitle>
      <AlertDescription className="flex flex-col gap-2">
        <div className="flex items-center gap-3">
          <div
            className="relative flex-1 h-2 rounded-full bg-muted overflow-hidden"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={percent ?? undefined}
            aria-label={label}
          >
            {percent !== null ? (
              <div
                className="absolute inset-y-0 left-0 bg-primary transition-[width] duration-300 ease-out"
                style={{ width: `${percent}%` }}
              />
            ) : (
              // Indeterminate: pulsing bar until the first response arrives.
              <div className="absolute inset-0 bg-primary/40 animate-pulse" />
            )}
          </div>
          <span className="text-xs tabular-nums font-medium min-w-[3rem] text-right">
            {percent !== null ? `${percent}%` : "…"}
          </span>
        </div>
        <div className="text-xs text-muted-foreground">
          {loaded.toLocaleString()}
          {hasTotal ? ` of ~${(total as number).toLocaleString()}` : ""} runs
          {currentDate ? (
            <>
              {" · fetching "}
              <span className="font-mono">{currentDate}</span>
            </>
          ) : null}
        </div>
      </AlertDescription>
    </Alert>
  );
}
